import {
  LeadEligibilityToggle,
  RecordActivity,
  SharedRecordManagement,
  VoiceCallCard,
  VoiceCallDetail,
  VoiceCallListItem,
  type RecordAnalysisEntry,
} from '@command-center/oneup-portal-ui';
import { callCategoryLabel, leadGradeLabel } from '@oneup/shared';
import {
  DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS,
  INCOMING_CALL_TRIAGE_KEY,
  callPurposeLabel,
  leadEligibilityLabel,
  normalizeIncomingCallTriageSettings,
  resolveLeadEligibility,
} from '@command-center/switchboard-sdk';
import {
  CollectionViewToggle,
  CompactRecordList,
  DateRangeFilter,
  FilterSelect,
  ListTabs,
  ListToolbar,
  Pagination,
  RecordCardGrid,
  RecordDetailShell,
  RecordTable,
  RecordTableCell,
  RecordTablePrimaryCell,
  RecordTableRow,
  SearchField,
  WorkspaceSplitView,
  dateRangeFromPreset,
  useCollectionView,
  useIsSplitView,
  usePageHeader,
} from '@command-center/ui-system';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Bot,
  PhoneCall,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@command-center/ui-system';
import {
  aiSystemsApi,
  voiceManagementApi,
  VOICE_CASCADE_NOT_CONFIGURED_MESSAGE,
  type AiAgentItem,
  type AiCallItem,
} from '../lib/ai-systems-api';
import { voiceManagementReadinessWarnings } from '../lib/voice-management-readiness';
import { switchboardApi } from '../lib/switchboard-api';
import {
  EmptyState,
  ErrorState,
  Field,
  IntakeStateBadge,
  LoadingState,
  formatDateTime,
} from './parts';
import { ManagedVoiceAgentsPanel, VoiceAgentManagementDialog } from '../components/VoiceAgentManagementDialog';
import {
  IntakeArchiveHeaderAction,
  IntakeRecordStatusBanner,
  useIntakeSourceState,
  type IntakeSourceState,
} from '../components/intake-action-banner';
import { IntakeActionCenter } from '../components/intake-action-center';
import { LinkedContactRecords } from '../components/linked-contact-records';
import { leadloopApi } from '../lib/leadloop-api';
import { BulkActionBar, SelectionCheckbox, useBulkSelection } from '../components/bulk-record-actions';

type VoiceAgentsTab = 'agents' | 'calls';

/** Matches the offset-paged views so page sizes feel the same across the app. */
const CALLS_PAGE_SIZE = 25;

export function VoiceAgentsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: VoiceAgentsTab = searchParams.get('tab') === 'agents' ? 'agents' : 'calls';

  const setTab = (next: VoiceAgentsTab) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (next === 'agents') params.set('tab', 'agents');
      else params.delete('tab');
      return params;
    });
  };

  /*
    The AppShell already prints this page's name from the navigation config.
    The PageIntro block that used to sit here repeated it — title, subtitle, and
    a decorative icon — so every Calls page opened with its own name twice.
  */
  usePageHeader(
    {
      title: 'Calls',
      subtitle: 'Call history, and the voice agents that answered.',
    },
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <ListTabs
        label="Call feeds"
        active={tab}
        onChange={(next) => setTab(next as VoiceAgentsTab)}
        tabs={[
          { id: 'calls', label: 'Calls', icon: <PhoneCall className="h-4 w-4" /> },
          { id: 'agents', label: 'Voice Agents', icon: <Bot className="h-4 w-4" /> },
        ]}
      />
      {tab === 'agents' ? <AiAgentsView /> : <AiCallsView />}
    </div>
  );
}

export function AiAgentsView() {
  const [managementOpen, setManagementOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<AiAgentItem | null>(null);
  const agents = useQuery({
    queryKey: ['ai-systems', 'agents', query],
    queryFn: () => aiSystemsApi.agents(query || undefined),
  });
  const managementReadiness = useQuery({
    queryKey: ['voice-management', 'readiness'],
    queryFn: voiceManagementApi.readiness,
    retry: false,
  });
  const managementAvailable = Boolean(
    managementReadiness.data?.enabled && managementReadiness.data.ready,
  );
  const managementWarnings = voiceManagementReadinessWarnings(
    managementReadiness.data,
    managementReadiness.error,
  );

  const items = agents.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <SourceWarnings warnings={agents.data?.warnings} />
      <SourceWarnings warnings={managementWarnings} />
      {managementAvailable && <ManagedVoiceAgentsPanel />}

      <ListToolbar
        search={(
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search agents…"
            label="Search voice agents"
          />
        )}
        filters={(
          <Button
            size="sm"
            disabled={!managementAvailable || managementReadiness.isLoading}
            title={managementAvailable ? 'Import or create a Cascade Voice Agent' : 'Complete Cascade Voice Agent readiness checks first'}
            onClick={() => setManagementOpen(true)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add Voice Agent
          </Button>
        )}
        count={`${items.length} ${items.length === 1 ? 'agent' : 'agents'}`}
      />

      <VoiceAgentManagementDialog open={managementOpen} onOpenChange={setManagementOpen} />

      {agents.isLoading ? (
        <LoadingState />
      ) : agents.isError ? (
        <ErrorState error={agents.error} />
      ) : items.length === 0 ? (
        <EmptyState label={VOICE_CASCADE_NOT_CONFIGURED_MESSAGE} />
      ) : (
        <RecordTable
          columns={[
            { key: 'agent', header: 'Agent' },
            { key: 'role', header: 'Role', hideOnMobile: true },
            { key: 'connection', header: 'Connection' },
            { key: 'calls', header: 'Calls', align: 'right' },
            { key: 'followup', header: 'Follow-up', align: 'right', hideOnMobile: true },
            { key: 'last', header: 'Last call', hideOnMobile: true },
          ]}
        >
          {items.map((agent) => (
            <RecordTableRow key={agent.id} onOpen={() => setSelected(agent)}>
              <RecordTablePrimaryCell
                title={agent.name}
                subtitle={agent.assignedClient ?? agent.type ?? 'Cascade'}
              />
              <RecordTableCell hideOnMobile>{agent.role ?? agent.type}</RecordTableCell>
              <RecordTableCell>
                <SystemStatusBadge value={agent.connectionStatus ?? agent.status} />
              </RecordTableCell>
              <RecordTableCell align="right">{agent.recentCallCount}</RecordTableCell>
              <RecordTableCell align="right" hideOnMobile>
                {agent.followUpCount ? <Badge variant="warning">{agent.followUpCount}</Badge> : undefined}
              </RecordTableCell>
              <RecordTableCell hideOnMobile>{formatDateTime(agent.lastCallAt)}</RecordTableCell>
            </RecordTableRow>
          ))}
        </RecordTable>
      )}

      <AgentDetail agent={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export function AiCallsView() {
  const [query, setQuery] = useState('');
  const [callView, setCallView] = useState('default');
  const [category, setCategory] = useState('all');
  const [dates, setDates] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useCollectionView('cascade:records:calls:view', 'list');
  const queryClient = useQueryClient();
  const switchboard = useQuery({
    queryKey: ['cascade', 'switchboard'],
    queryFn: switchboardApi.list,
  });
  const triageSettings = normalizeIncomingCallTriageSettings(
    switchboard.data?.items.find((entry) => entry.definition.key === INCOMING_CALL_TRIAGE_KEY)?.settings
      ?? DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS,
  );
  /*
    Calls page by cursor, not by page number: the voice store is shared with
    another portal and can only hand out "the next page", never a total. Cursors
    for pages already visited are kept so Previous works without re-walking the
    feed from the start. Any filter change resets both, since a cursor is only
    meaningful for the query that produced it.
  */
  const [callPage, setCallPage] = useState(1);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const callFilterKey = `${callView}|${category}|${showArchived}|${query}|${dates}`;
  const [appliedFilterKey, setAppliedFilterKey] = useState(callFilterKey);
  // Reset during render rather than in an effect, so a filter change never
  // fires a request carrying the previous filter's cursor.
  if (appliedFilterKey !== callFilterKey) {
    setAppliedFilterKey(callFilterKey);
    setCallPage(1);
    setCursors([undefined]);
  }
  const activePage = appliedFilterKey === callFilterKey ? callPage : 1;

  const calls = useQuery({
    queryKey: ['ai-systems', 'calls', callView, category, showArchived, query, dates, activePage],
    queryFn: () => aiSystemsApi.calls({
      q: query || undefined,
      view: callView,
      category: category === 'all' ? undefined : category,
      showArchived: callView === 'all' ? showArchived : false,
      dateFrom: dateRangeFromPreset(dates).from,
      limit: CALLS_PAGE_SIZE,
      cursor: cursors[activePage - 1],
    }),
    placeholderData: (previous) => previous,
  });

  const items = calls.data?.items ?? [];
  const actionableIds = items
    .filter((call) => call.lifecycle === 'new' || call.lifecycle === 'open')
    .map((call) => call.id);
  const selection = useBulkSelection(actionableIds);
  const bulk = useMutation({
    mutationFn: (action: 'create_leads' | 'dismiss') => leadloopApi.bulkIntake(
      selection.selectedIds.map((sourceId) => ({ sourceType: 'call', sourceId })),
      action,
      action === 'dismiss' ? { disposition: 'not_qualified' } : {},
    ),
    onSuccess: () => {
      selection.clear();
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-systems', 'calls'] }),
        queryClient.invalidateQueries({ queryKey: ['intake'] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
      ]);
    },
  });
  const nextCursor = calls.data?.nextCursor ?? undefined;
  const goToCallPage = (next: number) => {
    if (next > activePage) {
      // Record the cursor that opens the page being moved to, so Previous can
      // come back to it without replaying the feed from the beginning.
      if (!nextCursor) return;
      setCursors((current) => {
        const updated = [...current];
        updated[next - 1] = nextCursor;
        return updated;
      });
    }
    setCallPage(next);
  };
  const callPagination = (
    <Pagination
      page={activePage}
      pageSize={CALLS_PAGE_SIZE}
      count={items.length}
      hasNext={Boolean(nextCursor)}
      onPageChange={goToCallPage}
      busy={calls.isFetching}
      label="calls"
    />
  );
  const requestedCallId = searchParams.get('call');
  const listedCall = items.find((call) => call.id === requestedCallId);
  const linkedCall = useQuery({
    queryKey: ['voice-call-detail', requestedCallId],
    queryFn: () => aiSystemsApi.call(requestedCallId!),
    enabled: Boolean(requestedCallId && !listedCall),
  });
  const selected = listedCall ?? linkedCall.data ?? null;
  const selectedIntakeState = useIntakeSourceState('call', selected?.id);
  const setSelected = (call: AiCallItem | null) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (call) params.set('call', call.id);
      else params.delete('call');
      return params;
    });
  };
  const selectedIndex = selected ? items.findIndex((call) => call.id === selected.id) : -1;

  // Below the split the detail replaces the list, so its tabs and filters would
  // be sitting above a list that is no longer rendered.
  const listVisible = useIsSplitView() || !selected;

  return (
    <div className="flex flex-col gap-4">
      <SourceWarnings warnings={calls.data?.warnings} />

      {/*
        Two tabs, not three. "Low Value" was a tab for a bucket nobody chooses
        to go and read: a client asks "what needs me?" and, occasionally, "show
        me everything." The classification itself is untouched — it still keeps
        low-value calls out of Actionable, still shows as a pill on the record,
        and every one of those calls is still here under All.
      */}
      {listVisible && (
        <ListTabs
          label="Call feed"
          active={callView}
          onChange={setCallView}
          tabs={[
            { id: 'default', label: 'Actionable' },
            { id: 'all', label: 'All' },
          ]}
        />
      )}

      {listVisible && (
      <ListToolbar
        search={(
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search caller, phone, business, or summary…"
            label="Search calls"
          />
        )}
        filters={(
          <>
            <FilterSelect
              value={category}
              onChange={setCategory}
              label="Filter by call type"
              options={[
                { value: 'all', label: 'All types' },
                ...triageSettings.purposes
                  .filter((purpose) => purpose.enabled)
                  .map((purpose) => ({ value: purpose.key, label: purpose.label })),
              ]}
            />
            {callView === 'all' && (
              <label className="flex h-9 items-center gap-2 rounded-md border border-border bg-card px-2.5 text-[13px] text-foreground">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(event) => setShowArchived(event.target.checked)}
                />
                Include archived
              </label>
            )}
          </>
        )}
        dates={<DateRangeFilter value={dates} onChange={setDates} />}
        count={`${items.length} ${items.length === 1 ? 'call' : 'calls'}`}
        viewToggle={!selected && <CollectionViewToggle view={view} onChange={setView} />}
      />
      )}

      {!selected && (
        <BulkActionBar count={selection.selectedIds.length} busy={bulk.isPending} onClear={selection.clear}>
          <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate('create_leads')}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> Create Leads with AI
          </Button>
          <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => bulk.mutate('dismiss')}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden /> Dismiss
          </Button>
        </BulkActionBar>
      )}
      {bulk.error && (
        <p role="alert" className="text-sm text-destructive">
          {bulk.error instanceof Error ? bulk.error.message : 'The bulk action failed.'}
        </p>
      )}

      {calls.isLoading ? (
        <LoadingState />
      ) : calls.isError ? (
        <ErrorState error={calls.error} />
      ) : items.length === 0 ? (
        <EmptyState
          label={calls.data?.configuration
            ? VOICE_CASCADE_NOT_CONFIGURED_MESSAGE
            : 'No voice calls match this view.'}
        />
      ) : (
        <WorkspaceSplitView
          detail={selected ? (
            <CallDetailPane
              call={selected}
              intakeState={selectedIntakeState}
              onClose={() => setSelected(null)}
              onPrevious={selectedIndex > 0 ? () => setSelected(items[selectedIndex - 1]!) : undefined}
              onNext={selectedIndex >= 0 && selectedIndex < items.length - 1
                ? () => setSelected(items[selectedIndex + 1]!)
                : undefined}
            />
          ) : undefined}
        >
          {selected ? (
            /* A record is open: the list condenses so the detail gets the room. */
            <CompactRecordList title={`${items.length} calls on this page`}>
              {items.map((call) => (
                <VoiceCallListItem
                  key={`${call.source}:${call.id}`}
                  call={toSharedCall(call)}
                  selected={call.id === selected.id}
                  onSelect={() => setSelected(call)}
                />
              ))}
            </CompactRecordList>
          ) : view === 'cards' ? (
            <div className="flex flex-col gap-4">
              <RecordCardGrid>
                {items.map((call) => (
                  <div key={`${call.source}:${call.id}`} className="relative">
                    {actionableIds.includes(call.id) && (
                      <span className="absolute left-3 top-3 z-10 rounded bg-background/90 p-1">
                        <SelectionCheckbox
                          checked={selection.selected.has(call.id)}
                          onChange={() => selection.toggle(call.id)}
                          label={`Select ${call.callerName ?? 'call'}`}
                        />
                      </span>
                    )}
                    <VoiceCallCard
                      call={toSharedCall(call)}
                      onSelect={() => setSelected(call)}
                      badges={
                        <IntakeStateBadge
                          lifecycle={call.lifecycle}
                          resolutionLabel={call.resolutionLabel}
                        />
                      }
                    />
                  </div>
                ))}
              </RecordCardGrid>
              {callPagination}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
            <RecordTable
              columns={[
                {
                  key: 'select',
                  header: (
                    <SelectionCheckbox
                      checked={selection.allSelected}
                      onChange={selection.toggleAll}
                      label="Select all actionable Calls"
                    />
                  ),
                  className: 'w-10',
                },
                { key: 'caller', header: 'Caller' },
                { key: 'started', header: 'Started' },
                { key: 'state', header: 'State' },
                { key: 'lead', header: 'Lead' },
                { key: 'rating', header: 'Rating', hideOnMobile: true },
                { key: 'category', header: 'Category', hideOnMobile: true },
                { key: 'sentiment', header: 'Sentiment', hideOnMobile: true },
                { key: 'agent', header: 'Agent', hideOnMobile: true },
                { key: 'duration', header: 'Duration', align: 'right' },
              ]}
            >
              {items.map((call) => (
                <RecordTableRow
                  key={`${call.source}:${call.id}`}
                  selected={selection.selected.has(call.id)}
                  onOpen={() => setSelected(call)}
                >
                  <RecordTableCell>
                    {actionableIds.includes(call.id) && (
                      <SelectionCheckbox
                        checked={selection.selected.has(call.id)}
                        onChange={() => selection.toggle(call.id)}
                        label={`Select ${call.callerName ?? 'call'}`}
                      />
                    )}
                  </RecordTableCell>
                  <RecordTablePrimaryCell
                    title={call.callerName ?? call.businessName ?? call.phone ?? 'Unknown caller'}
                    subtitle={call.summary ?? call.reasonForCall}
                  />
                  <RecordTableCell>{formatDateTime(call.startedAt)}</RecordTableCell>
                  <RecordTableCell>
                    <IntakeStateBadge
                      lifecycle={call.lifecycle}
                      resolutionLabel={call.resolutionLabel}
                    />
                  </RecordTableCell>
                  <RecordTableCell>{leadGradeLabel(call.leadGrade)}</RecordTableCell>
                  <RecordTableCell hideOnMobile>
                    {call.sourceRating ? `${call.sourceRating} ${call.sourceRating === 1 ? 'Star' : 'Stars'}` : undefined}
                  </RecordTableCell>
                  <RecordTableCell hideOnMobile>
                    {call.callCategoryLabel ?? callCategoryLabel(call.callCategory)}
                  </RecordTableCell>
                  <RecordTableCell hideOnMobile>
                    {call.sentiment ? <span className="capitalize">{call.sentiment}</span> : undefined}
                  </RecordTableCell>
                  <RecordTableCell hideOnMobile>{call.agentName}</RecordTableCell>
                  <RecordTableCell align="right">{formatDuration(call.durationSeconds)}</RecordTableCell>
                </RecordTableRow>
              ))}
            </RecordTable>
            {callPagination}
            </div>
          )}
        </WorkspaceSplitView>
      )}

    </div>
  );
}

function SourceWarnings({ warnings }: { warnings?: string[] }) {
  if (!warnings?.length) return null;
  return (
    <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <div>
          <p className="font-medium">Connection notes</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function AgentDetail({ agent, onClose }: { agent: AiAgentItem | null; onClose: () => void }) {
  return (
    <Dialog open={Boolean(agent)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl">
        {agent && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-primary" />
                {agent.name}
              </DialogTitle>
              <DialogDescription>
                {agent.role ?? agent.type ?? 'Cascade AI agent'} · {agent.assignedClient ?? 'Cascade'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardContent className="grid gap-3 p-4">
                  <Field label="Status" value={<SystemStatusBadge value={agent.status} />} />
                  <Field label="Connection" value={<SystemStatusBadge value={agent.connectionStatus} />} />
                  <Field label="Purpose" value={agent.role} />
                  <Field label="Updated" value={formatDateTime(agent.lastModified)} />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="grid gap-3 p-4">
                  <Field label="Recent calls" value={agent.recentCallCount} />
                  <Field label="Follow-up" value={agent.followUpCount} />
                  <Field label="Errors" value={agent.errorCount} />
                  <Field label="Last call" value={formatDateTime(agent.lastCallAt)} />
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Call detail, rendered in the workspace's right pane rather than a modal so it
 * matches Leads and keeps the record list navigable beside it.
 */
function CallDetailPane({
  call,
  intakeState,
  onClose,
  onPrevious,
  onNext,
}: {
  call: AiCallItem;
  intakeState: IntakeSourceState;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['voice-call-detail', call.id],
    queryFn: () => aiSystemsApi.call(call.id),
    enabled: Boolean(call.id),
  });
  // Show the summary row immediately and let the detail fill in, so selecting a
  // call never blanks the pane while the request is in flight.
  const item = detail.data ?? call;
  const switchboard = useQuery({
    queryKey: ['cascade', 'switchboard'],
    queryFn: switchboardApi.list,
  });
  const triageSettings = normalizeIncomingCallTriageSettings(
    switchboard.data?.items.find((entry) => entry.definition.key === INCOMING_CALL_TRIAGE_KEY)?.settings
      ?? DEFAULT_INCOMING_CALL_TRIAGE_SETTINGS,
  );
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['voice-call-detail', call.id] }),
      queryClient.invalidateQueries({ queryKey: ['ai-systems', 'calls'] }),
      queryClient.invalidateQueries({ queryKey: ['cascade', 'leadloop'] }),
      queryClient.invalidateQueries({ queryKey: ['intake-source', 'call', call.id] }),
    ]);
  };

  /*
    What the toggle shows. An explicit stored decision wins; anything the
    analysis left undecided is answered by the automatic rule, so a reviewer
    never meets a call whose Lead question is blank.
  */
  const eligibility = resolveLeadEligibility(item.leadEligibility, {
    purpose: item.callCategory,
    transcript: item.transcript,
    durationSeconds: item.durationSeconds,
    lowValuePurposes: triageSettings.lowValuePurposes,
  });

  /*
    Once the call has become a Lead — or been linked to one — the toggle is
    asking a question that is already answered, and answering it again cannot
    undo the Lead. The banner above states the outcome and links to it, which is
    the only thing left to do from here.
  */
  const resolvedToLead = Boolean(intakeState.lead);

  return (
    <RecordDetailShell
      recordType="call"
      onClose={onClose}
      onPrevious={onPrevious}
      onNext={onNext}
    >
      {detail.isError && <ErrorState error={detail.error} />}
      <VoiceCallDetail
        call={toSharedCall(item)}
        transcript={item.transcript}
        // Cascade is client-facing: cost and provider internals stay hidden.
        // Only One Up Admin passes includeAdmin.
        includeAdmin={false}
        recording={item.recordingUrl ? <AuthorizedRecordingButton url={item.recordingUrl} /> : undefined}
        actions={(
          <IntakeArchiveHeaderAction
            archived={item.archived}
            onArchive={async (archive) => { await aiSystemsApi.archive(item.id, archive); await refresh(); }}
          />
        )}
        overviewBanner={(
          <IntakeRecordStatusBanner
            sourceType="call"
            state={intakeState}
          />
        )}
        actionCenter={(
          <IntakeActionCenter
            recordId={item.id}
            sourceType="call"
            state={intakeState}
            onAddNote={async (body) => { await aiSystemsApi.addNote(item.id, body); await refresh(); }}
          />
        )}
        eligibilityToggle={resolvedToLead ? undefined : (
          <LeadEligibilityToggle
            eligible={eligibility === 'eligible'}
            automatic={!item.leadEligibility || item.leadEligibility === 'needs_review'}
            onChange={async (next) => {
              /*
                The relationship and call type ride along unchanged: the triage
                write takes all three values, and this control owns only one of
                them. Sending the current values keeps the stored row whole.
              */
              await aiSystemsApi.updateTriage(item.id, {
                callerRelationship: item.callerRelationship ?? triageSettings.fallbackCallerRelationship,
                callPurpose: item.callCategory ?? triageSettings.fallbackPurpose,
                leadEligibility: next ? 'eligible' : 'not_eligible',
              });
              await refresh();
            }}
          />
        )}
        contact={(
          <LinkedContactRecords
            contacts={intakeState.contacts}
            loading={intakeState.contactsLoading}
          />
        )}
        activity={(
          <RecordActivity entries={callAnalysis(item)}>
            <SharedRecordManagement
              notes={item.notes}
              archived={item.archived}
              // Read-only here: notes are written in the Action Center on
              // Overview, and a second composer on this tab would look like a
              // different kind of note.
              notesReadOnly
              onAddNote={async (body) => { await aiSystemsApi.addNote(item.id, body); await refresh(); }}
              onArchive={async () => { await aiSystemsApi.archive(item.id, true); await refresh(); }}
              onRestore={async () => { await aiSystemsApi.archive(item.id, false); await refresh(); }}
            />
          </RecordActivity>
        )}
      />
    </RecordDetailShell>
  );
}

/**
 * The call's analysis history: what the triage Switch concluded and why, then
 * every manual correction since. This is the content the Assessment tab used to
 * hold, minus the controls — one of which is now the toggle on Overview and two
 * of which were decisions nobody should have been asked to make.
 */
function callAnalysis(call: AiCallItem): RecordAnalysisEntry[] {
  const entries: RecordAnalysisEntry[] = [];

  const verdict = [
    call.callCategoryLabel ?? callCategoryLabel(call.callCategory),
    leadGradeLabel(call.leadGrade),
  ]
    .filter(Boolean)
    .join(' · ');
  if (verdict || call.gradeExplanation) {
    entries.push({
      id: `triage:${call.id}`,
      source: 'Incoming Call Triage',
      result: verdict || undefined,
      explanation: call.gradeExplanation ?? call.triageReasoning ?? undefined,
      occurredAt: call.startedAt,
    });
  }

  for (const change of call.triageHistory ?? []) {
    entries.push({
      id: `correction:${change.id}`,
      source: 'Manual correction',
      result: [
        change.newCallPurpose ? callPurposeLabel(change.newCallPurpose) : undefined,
        change.newLeadEligibility ? leadEligibilityLabel(change.newLeadEligibility) : undefined,
      ]
        .filter(Boolean)
        .join(' · ') || undefined,
      occurredAt: change.changedAt,
      actor: change.changedByName,
    });
  }

  // Newest first: the current verdict is what a reader came to check.
  return entries.sort((a, b) => (b.occurredAt ?? '').localeCompare(a.occurredAt ?? ''));
}

/** Adapt Cascade's list DTO to the shared presentation input. */
function toSharedCall(call: AiCallItem) {
  return {
    id: call.id,
    callerName: call.callerName ?? call.businessName,
    // The business rides separately so the card can show it under the caller
    // name; the fallback above only covers a call with no person's name at all.
    companyName: call.businessName,
    customerNumber: call.phone,
    summary: call.summary,
    leadGrade: call.leadGrade,
    sourceRating: call.sourceRating,
    leadScore: call.leadScore,
    assistantScore: call.assistantScore,
    sentiment: call.sentiment,
    callCategory: call.callCategory,
    callCategoryLabel: call.callCategoryLabel,
    gradeExplanation: call.gradeExplanation,
    structuredOutputs: call.structuredOutputs,
    startedAt: call.startedAt,
    durationSeconds: call.durationSeconds,
    direction: call.direction ?? call.type,
    agentName: call.agentName,
    callerRelationship: call.callerRelationship,
    callerRelationshipLabel: call.callerRelationshipLabel,
    leadEligibility: call.leadEligibility,
    leadEligibilityLabel: call.leadEligibilityLabel,
    isLowValue: call.isLowValue,
    archived: call.archived,
  };
}


function AuthorizedRecordingButton({ url }: { url: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');

  const open = async () => {
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setState('error');
      return;
    }
    popup.opener = null;
    setState('loading');
    try {
      const objectUrl = await aiSystemsApi.openRecording(url);
      popup.location.replace(objectUrl);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10 * 60_000);
      setState('idle');
    } catch {
      popup.close();
      setState('error');
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <Button variant="outline" onClick={() => void open()} disabled={state === 'loading'}>
        {state === 'loading' ? 'Opening recording…' : 'Open recording'}
      </Button>
      {state === 'error' && <span className="text-xs text-destructive">Recording unavailable. Check sign-in and popup settings.</span>}
    </span>
  );
}

function SystemStatusBadge({ value }: { value?: string }) {
  if (!value) return <Badge variant="muted">Unknown</Badge>;
  const normalized = value.toLowerCase();
  const variant = /error|failed|spam|disconnected/.test(normalized)
    ? 'destructive'
    : /follow|review|warning|pending/.test(normalized)
      ? 'warning'
      : /active|connected|complete|answered|booked|ready/.test(normalized)
        ? 'success'
        : 'secondary';
  return <Badge variant={variant}>{value}</Badge>;
}

function formatDuration(seconds?: number): string {
  if (seconds === undefined || !Number.isFinite(seconds)) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.max(0, Math.round(seconds % 60));
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
}
